/**
 * Deterministically reconcile reader-visible whole-parcel FEMA claims against
 * an exact parcel-polygon area analysis.
 *
 * The FEMA provider retains a single highest-risk/query-point `floodZone` for
 * backwards compatibility. That headline must never be expanded into "the
 * entire site is Zone A" when the same evidence contains an exact multi-zone
 * polygon result. This pass corrects only that narrow contradiction and leaves
 * the grounding mismatch detector in place as a backstop.
 */
import { blankFloodDisclosurePhrases, floodClauseDeniesClaim, floodClausePolarityIsAmbiguous, isRewriterSentenceEnd, preChangeRewriterWouldStandDown } from '../floodDisclosurePhrases';
import type { FloodAreaAnalysis } from '../../types/siteEvidence';
import type { SiteRagReport } from '../../types/siteRag';

type FloodAreaAnalysisInput = Pick<
  FloodAreaAnalysis,
  'method' | 'parcelAreaAcres' | 'unmappedFraction' | 'closureErrorFraction' | 'zoneAreas' | 'geometryTier' | 'precisionSnapDeg'
>;

interface ValidAreaAnalysis {
  zoneAreas: Array<{ zone: string; acres: number; fraction: number }>;
  unmappedFraction: number;
  mixText: string;
  /** Disclosed geometry fallback tier, when the intersection was not exact. */
  geometryTier?: 'single-sweep' | 'precision-snap';
  precisionSnapDeg?: number;
}

export interface FloodAreaNarrativeReconciliation {
  report: SiteRagReport;
  correctedCount: number;
  mixText?: string;
}

const EFFECTIVELY_WHOLE_FRACTION = 0.995;

// These containers preserve evidence, provenance, QA, and source claims. They
// are deliberately excluded even if a quoted source happens to contain the
// same words as a reader-visible narrative defect.
const NON_NARRATIVE_CONTAINER_KEYS = new Set([
  'claims',
  'datasets',
  'documents',
  'evidence',
  'generationProvenance',
  'inputProvenance',
  'metadata',
  'providerSnapshots',
  'qaFindings',
  'quality',
  'qualityHoldFindings',
  'references',
  'researchSnapshot',
  'reviewIssues',
  'sanityFindings',
  'scoringInfluence',
  'siteContextAtGen',
  'sourceClaims',
  'sources',
]);

const FEMA_ZONE = String.raw`(?:FEMA(?:'s)?\s+)?(?:flood\s+)?zone\s*[:=\-]?\s*["'“”‘’]?\s*(A(?:E|H|O|R|99)?|V(?:E)?|X(?:500)?|B|C|D)\b["'“”‘’]?`;

// Each expression captures only the false absolute zone clause. Any trailing
// wetlands, permitting, cost, or citation text remains byte-for-byte intact.
export const WHOLE_PARCEL_ZONE_PATTERNS = [
  // The quantifier arms run FIRST and the bare-head arm refuses a quantifier
  // lead: otherwise "all of THE PROPERTY is located wholly within <zone>"
  // matched only its inner noun phrase, the substitution landed inside the
  // quantifier ("all of <the measured mix>"), and every lead-based guard read
  // a lead ending in "all of " and could not fire.
  new RegExp(
    String.raw`\b(?:all|100\s*%)\s+(?:of\s+)?(?:the\s+)?(?:project\s+)?(?:site|parcel|property)\s+(?:is|lies|sits|falls)\s+(?:located\s+)?(?:(?:entirely|wholly)\s+)?(?:within|in)\s+${FEMA_ZONE}`,
    'giu',
  ),
  new RegExp(
    String.raw`(?<!\b(?:all|100\s*%)\s+of\s+)(?<!\b(?:all|100\s*%)\s+of\s+the\s+)(?<!\b(?:all|100\s*%)\s+the\s+)(?<!\b(?:all|100\s*%)\s+)\b(?:the\s+)?(?:project\s+)?(?:site|parcel|property)\s+(?:is|lies|sits|falls)\s+(?:located\s+)?(?:entirely|wholly)\s+(?:within|in)\s+${FEMA_ZONE}`,
    'giu',
  ),
  new RegExp(
    String.raw`\b(?:the\s+)?(?:entire|whole)\s+(?:project\s+)?(?:site|parcel|property)\s+(?:is|lies|sits|falls)\s+(?:located\s+)?(?:(?:entirely|wholly)\s+)?(?:within|in)\s+${FEMA_ZONE}`,
    'giu',
  ),
];

function normalizeZone(zone: string): string {
  return zone.trim().toUpperCase().replace(/[\s_-]+/gu, '');
}

function finiteFraction(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 1;
}

/** Never describes a fallback-tier intersection as exact. */
function analysisLabel(area: ValidAreaAnalysis): string {
  if (area.geometryTier === 'precision-snap') {
    return `FEMA parcel-polygon analysis recomputed on a ${area.precisionSnapDeg != null ? `${area.precisionSnapDeg}-degree ` : ''}coordinate grid after an exact-precision clipping failure (complete coverage is not claimed)`;
  }
  if (area.geometryTier === 'single-sweep') {
    return 'FEMA parcel-polygon analysis completed with a single-sweep union after an exact-precision clipping failure (complete coverage is not claimed)';
  }
  return 'Exact FEMA parcel-polygon analysis';
}

function validAreaAnalysis(
  area: FloodAreaAnalysisInput | null | undefined,
): ValidAreaAnalysis | null {
  if (
    area?.method !== 'polygon-intersection'
    || typeof area.parcelAreaAcres !== 'number'
    || !Number.isFinite(area.parcelAreaAcres)
    || area.parcelAreaAcres <= 0
    || typeof area.closureErrorFraction !== 'number'
    || !Number.isFinite(area.closureErrorFraction)
    || Math.abs(area.closureErrorFraction) > 0.01
    || !Array.isArray(area.zoneAreas)
    || area.zoneAreas.length === 0
  ) return null;

  const zoneAreas = area.zoneAreas.map((zone) => ({
    zone: String(zone.zone ?? '').trim(),
    acres: zone.acres,
    fraction: zone.fraction,
  }));
  if (zoneAreas.some((zone) => (
    !zone.zone
    || typeof zone.acres !== 'number'
    || !Number.isFinite(zone.acres)
    || zone.acres < 0
    || !finiteFraction(zone.fraction)
  ))) return null;

  const unmappedFraction = finiteFraction(area.unmappedFraction) ? area.unmappedFraction : 0;
  const closure = zoneAreas.reduce((sum, zone) => sum + zone.fraction, 0) + unmappedFraction;
  if (Math.abs(closure - 1) > 0.02) return null;

  const rows = zoneAreas
    .slice()
    .sort((a, b) => b.fraction - a.fraction)
    .map((zone) => `Zone ${zone.zone} ${zone.acres.toFixed(2)} ac (${(zone.fraction * 100).toFixed(1)}%)`);
  if (unmappedFraction > 0.0005) rows.push(`unmapped ${(unmappedFraction * 100).toFixed(1)}%`);
  const mixText = rows.length <= 1
    ? rows[0] ?? ''
    : `${rows.slice(0, -1).join(', ')}, and ${rows.at(-1)}`;
  const tier = area.geometryTier === 'single-sweep' || area.geometryTier === 'precision-snap' ? area.geometryTier : undefined;
  return mixText
    ? { zoneAreas, unmappedFraction, mixText, ...(tier ? { geometryTier: tier, ...(typeof area.precisionSnapDeg === 'number' ? { precisionSnapDeg: area.precisionSnapDeg } : {}) } : {}) }
    : null;
}

/**
 * The rewriter's bounds, from the same module as the gate's but biased LONG:
 * an abbreviation never ends the clause here, so a denial after one is always
 * inside the text the polarity guard reads.
 */
function terminatorsIn(text: string, from = 0, to = text.length): number[] {
  const found: number[] = [];
  for (let i = from; i < to; i += 1) if (isRewriterSentenceEnd(text, i)) found.push(i);
  return found;
}
function sentenceBounds(text: string, offset: number, matchLength: number): [number, number] {
  const priorTerminators = terminatorsIn(text, 0, offset);
  const priorBoundary = priorTerminators.length > 0 ? priorTerminators[priorTerminators.length - 1]! : -1;
  const afterOffset = offset + matchLength;
  const next = terminatorsIn(text, afterOffset);
  const remainingLength = text.length - afterOffset;
  const nextBoundary = next.length > 0 ? next[0]! - afterOffset : remainingLength;
  return [priorBoundary + 1, afterOffset + nextBoundary + (nextBoundary < remainingLength ? 1 : 0)];
}

/**
 * True when the claim itself is negated ("the site does NOT lie entirely
 * within Zone X"). Such a sentence asserts nothing false, so it needs neither
 * a substitution nor an appended correction.
 */
function isGenuinelyNegated(text: string, offset: number, matchLength: number): boolean {
  // HEAD's bounds, not this control's long-biased ones: the denial test needs
  // to see a question that ends the claim's own sentence ("Is mitigation
  // required, and <claim>?") without seeing one that a merged abbreviation
  // pulled in from the next sentence ("... per Fig. Is a LOMR required?").
  const [start, end] = headSentenceBounds(text, offset, matchLength);
  // The matched claim already spans subject, verb and zone token, so the
  // predicate ends with it: a negation AFTER it belongs to another subject
  // ("...Zone X, and the substation pad is not located within the floodway").
  return floodClauseDeniesClaim(text.slice(start, end), offset - start, offset - start + matchLength);
}

function isNonAssertiveContext(text: string, offset: number, matchLength: number): boolean {
  const [start, end] = sentenceBounds(text, offset, matchLength);
  const rawSentence = text.slice(start, end);
  // This control REWRITES prose, so it is deliberately conservative: any
  // negation, denial or interrogative anywhere in the assertion's clause makes
  // the polarity ambiguous, and substituting inside such a clause produces a
  // sentence that denies the true result ("It is inaccurate to state that
  // <the true zone mix>"). Those claims are still caught by the blocking
  // grounding gate, which production runs with REPORT_GROUNDING on.
  return floodClausePolarityIsAmbiguous(rawSentence, offset - start, offset - start + matchLength);
}

/**
 * The pre-change rewriter's own floor, on HEAD 47481c8d7's OWN bounds (not
 * this control's long-biased ones) and on the BLANKED text.
 *
 * The blanking is the one deliberate exception to "never less conservative
 * than the control this replaced", and it is the exception this whole change
 * exists to make: the pre-change rewriter read the platform's OWN mandated
 * disclosure ("complete coverage is not claimed") as a denial and stood down,
 * leaving a false whole-parcel claim uncorrected beside it. Blanking that
 * wording — and only that wording, which this system emits itself — restores
 * the correction. Every other token in the frozen predicate still floors this
 * control literally.
 */
function preChangeRewriterStoodDown(text: string, offset: number, matchLength: number): boolean {
  const [start, end] = headSentenceBounds(text, offset, matchLength);
  const blanked = blankFloodDisclosurePhrases(text.slice(start, end));
  return preChangeRewriterWouldStandDown(blanked, blanked.slice(0, offset - start));
}

/**
 * HEAD 47481c8d7's own bounds: the nearest '.', '!', '?' or newline either
 * side, with no whitespace test and no abbreviation list. The floor must be
 * read on THESE, not on this control's long-biased ones — a superset sentence
 * can stop ending in '?', and HEAD's trailing-question arm is then lost
 * exactly where it fired. The long bias stays for the denial-vocabulary scan
 * it was added for.
 */
function headSentenceBounds(text: string, offset: number, matchLength: number): [number, number] {
  const before = text.slice(0, offset);
  const prior = Math.max(before.lastIndexOf('.'), before.lastIndexOf('!'), before.lastIndexOf('?'), before.lastIndexOf('\n'));
  const afterOffset = offset + matchLength;
  const remaining = text.slice(afterOffset);
  const candidates = ['.', '!', '?', '\n'].map((c) => remaining.indexOf(c)).filter((i) => i >= 0);
  const next = candidates.length > 0 ? Math.min(...candidates) : remaining.length;
  return [prior + 1, afterOffset + next + (next < remaining.length ? 1 : 0)];
}

/**
 * True when nothing but leading punctuation stands between the start of the
 * claim's sentence and the claim itself.
 */
function claimIsMainClause(text: string, offset: number, matchLength: number): boolean {
  const [start] = sentenceBounds(text, offset, matchLength);
  return /^[\s"'\u201c\u201d\u2018\u2019(\[\-\u2013\u2014*_#>\u2022]*$/u.test(text.slice(start, offset));
}

function rewriteNarrativeString(text: string, area: ValidAreaAnalysis): {
  text: string;
  correctedCount: number;
} {
  let out = text;
  let correctedCount = 0;
  let queuedAppends = 0;
  // Whether the mix was ALREADY in the host before this pass. Testing the
  // output cannot tell that apart from a substitution this pass just made.
  const mixWasPresent = text.includes(area.mixText);

  for (const pattern of WHOLE_PARCEL_ZONE_PATTERNS) {
    pattern.lastIndex = 0;
    out = out.replace(pattern, (...args: unknown[]) => {
      const match = String(args[0] ?? '');
      const claimedZone = String(args[1] ?? '');
      const offset = Number(args.at(-2));
      const source = String(args.at(-1) ?? out);
      if (!Number.isFinite(offset)) return match;

      const normalized = normalizeZone(claimedZone);
      const claimedFraction = area.zoneAreas.reduce(
        (sum, zone) => sum + (normalizeZone(zone.zone) === normalized ? zone.fraction : 0),
        0,
      );
      if (claimedFraction >= EFFECTIVELY_WHOLE_FRACTION) return match;

      // SUBSTITUTE IN PLACE ONLY WHERE THE CLAIM IS THE SENTENCE'S OWN MAIN
      // CLAUSE — nothing before it but leading punctuation.
      //
      // Five rounds tried to classify the hosts that must NOT be rewritten in
      // place: frames, then complementisers, then verbs of saying, then the
      // shape of the lead's end. Each was escaped by its first unlisted member
      // — the last by a split infinitive ("premature to FIRMLY declare") and a
      // gerund ("so CLAIMING <claim> is premature"). Every escape substituted
      // the measured mix inside a sentence that denies it AND, because this
      // control runs before any detector snapshots the body, erased a claim the
      // gate would have blocked.
      //
      // Position cannot be escaped by vocabulary. The cost is that a claim with
      // any lead is corrected by APPENDING the measured mix rather than by
      // replacing the claim: the false sentence stays in the prose with the
      // measurement after it, and the blocking gate still fails closed on it.
      if (!claimIsMainClause(source, offset, match.length)
          || preChangeRewriterStoodDown(source, offset, match.length)
          || isNonAssertiveContext(source, offset, match.length)) {
        if (isGenuinelyNegated(source, offset, match.length)) return match;
        queuedAppends += 1;
        correctedCount += 1;
        return match;
      }

      correctedCount += 1;
      return `${analysisLabel(area)} reports ${area.mixText}`;
    });
    pattern.lastIndex = 0;
  }

  // The count reports corrections actually EMITTED. When the host ALREADY
  // carried the measured mix the append is suppressed, and every queued append
  // is withdrawn from the count. A mix this pass substituted does not suppress
  // it: those are different events (round-30 X30S-3, round-31 X31C-3).
  if (queuedAppends > 0 && mixWasPresent) {
    correctedCount = Math.max(0, correctedCount - queuedAppends);
    queuedAppends = 0;
  }
  if (queuedAppends > 0) {
    // The host keeps its own wording; the correction follows as its own
    // sentence, with a separator when the host has no terminal punctuation.
    const host = out.replace(/\s+$/u, '');
    const separator = /[.!?:;]$/u.test(host) ? ' ' : '. ';
    out = `${host}${separator}${analysisLabel(area)} reports ${area.mixText}.`.replace(/\.\.$/u, '.');
  }
  return { text: out, correctedCount };
}

function rewriteNarrativeValue(
  value: unknown,
  area: ValidAreaAnalysis,
): { value: unknown; correctedCount: number } {
  if (typeof value === 'string') {
    const rewritten = rewriteNarrativeString(value, area);
    return { value: rewritten.text, correctedCount: rewritten.correctedCount };
  }
  if (Array.isArray(value)) {
    let correctedCount = 0;
    const next = value.map((item) => {
      const rewritten = rewriteNarrativeValue(item, area);
      correctedCount += rewritten.correctedCount;
      return rewritten.value;
    });
    return { value: correctedCount > 0 ? next : value, correctedCount };
  }
  if (!value || typeof value !== 'object') return { value, correctedCount: 0 };

  let correctedCount = 0;
  const input = value as Record<string, unknown>;
  const next: Record<string, unknown> = { ...input };
  for (const [key, child] of Object.entries(input)) {
    if (NON_NARRATIVE_CONTAINER_KEYS.has(key)) continue;
    const rewritten = rewriteNarrativeValue(child, area);
    if (rewritten.correctedCount > 0) next[key] = rewritten.value;
    correctedCount += rewritten.correctedCount;
  }
  return { value: correctedCount > 0 ? next : value, correctedCount };
}

/**
 * Correct every duplicated reader-visible projection of a false whole-parcel
 * FEMA zone claim. Returns the original report by identity when no safe,
 * evidence-backed correction is available or required.
 */
export function reconcileWholeParcelFloodZoneNarrative(
  report: SiteRagReport,
  area: FloodAreaAnalysisInput | null | undefined,
): FloodAreaNarrativeReconciliation {
  const valid = validAreaAnalysis(area);
  if (!valid) return { report, correctedCount: 0 };
  const rewritten = rewriteNarrativeValue(report, valid);
  if (rewritten.correctedCount === 0) {
    return { report, correctedCount: 0, mixText: valid.mixText };
  }
  return {
    report: rewritten.value as SiteRagReport,
    correctedCount: rewritten.correctedCount,
    mixText: valid.mixText,
  };
}
